
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../../hooks/useApi';
import { theme, animations } from '../../styles/theme';
import React, { useState, useEffect } from 'react';
import BannerCarousel from './BannerCarousel';
import PWAInstallBanner from '../PWA/PWAInstallBanner';

import ApiService from '../../services/ApiService';

const MEDIA_BASE_URL = (
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  ''
).replace(/\/$/, '');

const extractImageUrl = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = extractImageUrl(item);
      if (nested) return nested;
    }
    return '';
  }

  if (typeof value === 'object') {
    const direct = value.url || value.src || value.image || '';
    if (typeof direct === 'string' && direct.trim()) {
      return direct.trim();
    }

    const formatUrl =
      value.formats?.large?.url ||
      value.formats?.medium?.url ||
      value.formats?.small?.url ||
      value.formats?.thumbnail?.url ||
      '';
    if (typeof formatUrl === 'string' && formatUrl.trim()) {
      return formatUrl.trim();
    }

    return extractImageUrl(value.data) || extractImageUrl(value.attributes);
  }

  return '';
};

const withMediaBaseUrl = (url = '') => {
  const cleanUrl = String(url || '').trim();
  if (!cleanUrl) return '';

  if (
    cleanUrl.startsWith('http://') ||
    cleanUrl.startsWith('https://') ||
    cleanUrl.startsWith('data:') ||
    cleanUrl.startsWith('blob:')
  ) {
    return cleanUrl;
  }

  if (cleanUrl.startsWith('//')) {
    return `https:${cleanUrl}`;
  }

  if (!MEDIA_BASE_URL) {
    return cleanUrl;
  }

  return `${MEDIA_BASE_URL}${cleanUrl.startsWith('/') ? '' : '/'}${cleanUrl}`;
};

const getProductImageUrl = (product) => {
  if (!product) return '';

  const rawUrl =
    extractImageUrl(product.image) ||
    extractImageUrl(product.productImage) ||
    extractImageUrl(product.imageUrl) ||
    extractImageUrl(product.thumbnail) ||
    extractImageUrl(product.media) ||
    extractImageUrl(product.attributes?.image) ||
    extractImageUrl(product.attributes?.productImage);

  return withMediaBaseUrl(rawUrl);
};

const buildInlinePlaceholder = (label = 'Gift Card') => {
  const safeLabel = String(label || 'Gift Card').slice(0, 28);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="450" viewBox="0 0 300 450"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#1e293b"/><stop offset="100%" stop-color="#334155"/></linearGradient></defs><rect width="300" height="450" fill="url(#g)"/><rect x="20" y="20" width="260" height="410" rx="18" fill="none" stroke="rgba(255,255,255,0.18)"/><text x="150" y="215" text-anchor="middle" fill="rgba(255,255,255,0.92)" font-family="Arial, sans-serif" font-size="18" font-weight="700">${safeLabel}</text><text x="150" y="245" text-anchor="middle" fill="rgba(255,255,255,0.6)" font-family="Arial, sans-serif" font-size="12">Gift Card</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const getBrandImageUrl = (favorite) => {
  const rawUrl =
    extractImageUrl(favorite?.attributes?.brandImage) ||
    extractImageUrl(favorite?.brandImage) ||
    extractImageUrl(favorite?.attributes?.image) ||
    extractImageUrl(favorite?.image);

  return withMediaBaseUrl(rawUrl);
};

// ──────────────────────────────────────────
// Quick-Action SVG Icons (no emojis, no deps)
// ──────────────────────────────────────────
const ScanIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="6" height="6" rx="1"/>
    <rect x="15" y="3" width="6" height="6" rx="1"/>
    <rect x="3" y="15" width="6" height="6" rx="1"/>
    <path d="M15 15h1.5v1.5"/>
    <path d="M15 21h6v-6"/>
    <path d="M21 15v.01"/>
    <path d="M12 3v3"/>
    <path d="M3 12h3"/>
    <path d="M12 12v.01"/>
  </svg>
);

const BalanceIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="6" width="20" height="13" rx="2"/>
    <path d="M2 10h20"/>
    <circle cx="7" cy="15" r="1" fill="currentColor"/>
    <rect x="12" y="13" width="6" height="2" rx="1" fill="currentColor" stroke="none"/>
  </svg>
);

const RedeemIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 12 20 22 4 22 4 12"/>
    <rect x="2" y="7" width="20" height="5" rx="1"/>
    <path d="M12 22V7"/>
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const InventoryIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h20v5H2z"/>
    <path d="M4 8v13h16V8"/>
    <path d="M10 12h4"/>
  </svg>
);

// ── Spin-card attention animations ───────────────────────────────
const spinCardFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-5px); }
`;

const spinCardGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,215,0,0), 0 4px 18px rgba(255,215,0,0.18); }
  50%       { box-shadow: 0 0 0 10px rgba(255,215,0,0), 0 8px 38px rgba(255,215,0,0.55); }
`;

const spinCardShimmer = keyframes`
  0%   { left: -100%; }
  60%  { left: 160%; }
  100% { left: 160%; }
`;

const spinCardRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.6; }
  100% { transform: scale(1.75); opacity: 0; }
`;

const spinWordGradientSlide = keyframes`
  0%   { background-position: 0% center; }
  100% { background-position: 200% center; }
`;

// ── Action-widget micro-animations ───────────────────────────
const widgetPress = keyframes`
  0%  { transform: scale(1); }
  50% { transform: scale(0.96); }
  100%{ transform: scale(1); }
`;

// ── Meteor shower animations ──────────────────────────────────
// Each keyframe fires a quick streak at a different angle then idles
const meteorA = keyframes`
  0%, 7%   { opacity: 0; transform: rotate(28deg) translateY(-110px) scaleY(0.2); }
  9%       { opacity: 1; transform: rotate(28deg) translateY(0px)    scaleY(1); }
  13%      { opacity: 0; transform: rotate(28deg) translateY(110px)  scaleY(0.2); }
  13.01%, 100% { opacity: 0; transform: rotate(28deg) translateY(110px) scaleY(0.2); }
`;
const meteorB = keyframes`
  0%, 7%   { opacity: 0; transform: rotate(-42deg) translateY(-130px) scaleY(0.2); }
  9%       { opacity: 1; transform: rotate(-42deg) translateY(0px)    scaleY(1); }
  13%      { opacity: 0; transform: rotate(-42deg) translateY(130px)  scaleY(0.2); }
  13.01%, 100% { opacity: 0; transform: rotate(-42deg) translateY(130px) scaleY(0.2); }
`;
const meteorC = keyframes`
  0%, 7%   { opacity: 0; transform: rotate(55deg) translateY(-90px)  scaleY(0.2); }
  9%       { opacity: 1; transform: rotate(55deg) translateY(0px)    scaleY(1); }
  13%      { opacity: 0; transform: rotate(55deg) translateY(90px)   scaleY(0.2); }
  13.01%, 100% { opacity: 0; transform: rotate(55deg) translateY(90px)  scaleY(0.2); }
`;
const meteorD = keyframes`
  0%, 7%   { opacity: 0; transform: rotate(-18deg) translateY(-150px) scaleY(0.2); }
  9%       { opacity: 1; transform: rotate(-18deg) translateY(0px)    scaleY(1); }
  13%      { opacity: 0; transform: rotate(-18deg) translateY(150px)  scaleY(0.2); }
  13.01%, 100% { opacity: 0; transform: rotate(-18deg) translateY(150px) scaleY(0.2); }
`;
const meteorE = keyframes`
  0%, 7%   { opacity: 0; transform: rotate(72deg)  translateY(-100px) scaleY(0.2); }
  9%       { opacity: 1; transform: rotate(72deg)  translateY(0px)    scaleY(1); }
  13%      { opacity: 0; transform: rotate(72deg)  translateY(100px)  scaleY(0.2); }
  13.01%, 100% { opacity: 0; transform: rotate(72deg)  translateY(100px) scaleY(0.2); }
`;

const MeteorLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
`;

const Meteor = styled.div`
  position: absolute;
  width: ${p => p.$w || '1px'};
  height: ${p => p.$h || '220px'};
  top: ${p => p.$top};
  left: ${p => p.$left};
  background: linear-gradient(
    to bottom,
    transparent 0%,
    ${p => p.$c1 || 'rgba(255,215,0,0.28)'} 38%,
    ${p => p.$c2 || 'rgba(255,140,0,0.16)'} 65%,
    transparent 100%
  );
  opacity: 0;
  pointer-events: none;
  animation: ${p => p.$anim} ${p => p.$dur || '14s'} ease-in-out ${p => p.$delay || '0s'} infinite;
`;

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: ${theme.gradients.background};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${theme.colors.backgroundStars};
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${animations.sparkle} 4s ease-in-out infinite;
  }
`;

const DashboardContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem ${theme.spacing.xl};
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: 100%;

  @media (max-width: 1400px) {
    padding: 2rem clamp(1.5rem, 3vw, 2rem);
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.75rem;
  }
`;

const SectionTitle = styled.h2`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.xl};
  font-weight: ${theme.typography.fontWeights.semibold};
  margin: 0 0 ${theme.spacing.md} 0;
  text-align: left;
`;

const QuickActionsSection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xl};
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.medium};
  }
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  box-sizing: border-box;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ActionCard = styled.div`
  position: relative;
  overflow: hidden;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: ${theme.borderRadius.medium};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  transition:
    border-color 0.25s ease,
    transform 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease;
  animation: ${animations.fadeIn} 0.4s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;
  box-sizing: border-box;

  &:hover {
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.07);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(-1px);
    transition-duration: 0.1s;
  }
`;

const ActionIconWrapper = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: ${theme.borderRadius.medium};
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid rgba(255,255,255,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary};
  transition: border-color 0.25s ease;

  svg { width: 20px; height: 20px; }

  ${ActionCard}:hover & {
    border-color: ${theme.colors.primary};
  }
`;

const ActionTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

const ActionArrow = styled.div``; /* kept for compat */

const ActionChevron = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.15);
  transition: color 0.25s ease, transform 0.25s ease;

  ${ActionCard}:hover & {
    color: ${theme.colors.primary};
    transform: translateX(3px);
  }
`;

/* keep alias so nothing else breaks */
const ActionIcon = ActionIconWrapper;

const ActionTitle = styled.h3`
  margin: 0;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ActionDescription = styled.p`
  margin: 0;
  color: ${theme.colors.textMuted};
  font-size: 11px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ── Header banner animations ─────────────────────────────────

const titleGradientSlide = keyframes`
  0%   { background-position: 0% center; }
  100% { background-position: 200% center; }
`;

const livePulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
`;

// Header Section
const DashboardHeader = styled.div`
  position: relative;
  overflow: hidden;
  background: transparent;
  backdrop-filter: blur(0px);

  /* Cut-corner HUD shape: shave bottom-right corner */
  clip-path: polygon(
    0 0,
    100% 0,
    100% calc(100% - 22px),
    calc(100% - 22px) 100%,
    0 100%
  );
  border-radius: 0;

  /* sharp left accent line — brand gold */
  border-left: 3px solid rgba(255, 215, 0, 0);
  border-top: 1px solid rgba(255, 215, 0, 0);
  border-right: 1px solid rgba(255, 255, 255, 0);
  border-bottom: 1px solid rgba(255, 255, 255, 0);

  padding: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  margin-top: ${theme.spacing.md};
  box-sizing: border-box;

  /* subtle dot-grid texture */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
    z-index: 0;
  }

  /* diagonal accent slash — gold to orange */
 

  > * { position: relative; z-index: 1; }

  @media (max-width: 768px) {
    padding: ${theme.spacing.lg};
    margin-bottom: ${theme.spacing.lg};
    clip-path: polygon(
      0 0, 100% 0,
      100% calc(100% - 14px),
      calc(100% - 14px) 100%,
      0 100%
    );
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.md};
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  gap: ${theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.md};
    margin-top: ${theme.spacing.sm};
    margin-bottom: ${theme.spacing.sm};
  }
`;

const HeaderBranding = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  @media (max-width: 768px) {
    width: 100%;
  }

  @media (max-width: 480px) {
    gap: ${theme.spacing.sm};
  }
`;

const HeaderLogo = () => (
  <img src="images/GGlogo.svg" alt="GG Trophy" style={{ height: '52px', display: 'block' }} />
);

const HeaderTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

/* tiny status badge above title */
const HeaderBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  padding: 3px 9px;
  border-radius: 4px;
  background: rgba(255, 215, 0, 0.08);
  border: 1px solid rgba(255, 215, 0, 0.28);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 215, 0, 0.8);

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${theme.colors.success};
    flex-shrink: 0;
    animation: ${livePulse} 2s ease-in-out infinite;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: clamp(1.5rem, 3.5vw, 2.2rem);
  font-weight: 900;
  letter-spacing: -0.03em;
  line-height: 1.05;

  /* white → brand gold → brand orange */
  background: linear-gradient(
    100deg,
    #ffffff 0%,
    #FFD700 55%,
    #FF8C00 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${titleGradientSlide} 6s linear infinite;
`;

const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: clamp(0.72rem, 1.3vw, 0.82rem);
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
`;

const resolveAvatarUrl = (url) => {
  if (!url) return null;
  const s = String(url || '').trim();
  if (!s) return null;
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:') || s.startsWith('blob:')) return s;
  if (!MEDIA_BASE_URL) return s;
  return `${MEDIA_BASE_URL}${s.startsWith('/') ? '' : '/'}${s}`;
};

const DashUserAvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const DashUserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.$hasImage ? 'transparent' : theme.gradients.primary};
  border: 3px solid ${props => props.$frameColor || 'rgba(255, 215, 0, 0.3)'};
  box-shadow: 0 4px 16px ${props => props.$frameColor ? `${props.$frameColor}55` : 'rgba(255, 215, 0, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: bold;
  color: #000;
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
`;

const DashUserAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const DashUserBadge = styled.div`
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${theme.gradients.primary};
  border: 2px solid rgba(10, 10, 15, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 50%;
  }
`;

const UserGreeting = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeights.semibold};
  font-size: clamp(0.9rem, 1.5vw, 1rem);
  white-space: nowrap;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    gap: ${theme.spacing.sm};
  }

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const HeaderPoints = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-weight: 800;
  font-size: clamp(0.95rem, 1.8vw, 1.1rem);
  color: #fff;
  letter-spacing: 0.01em;
  white-space: nowrap;

  /* left accent tick — brand gold */
  border-left: 3px solid rgba(255, 215, 0, 0.75);

  img {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const RedeemButton = styled.button`
  position: relative;
  overflow: hidden;
  background: ${theme.gradients.primary};
  color: #000;
  border: none;
  padding: 11px 26px;
  border-radius: 8px;

  /* matching cut-corner on button */
  clip-path: polygon(
    0 0, 100% 0,
    100% calc(100% - 8px),
    calc(100% - 8px) 100%,
    0 100%
  );

  cursor: pointer;
  font-weight: 800;
  font-size: clamp(0.82rem, 1.4vw, 0.9rem);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  transition: filter 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
  min-height: 44px;
  box-sizing: border-box;

  /* shimmer sweep */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: -75%;
    width: 50%; height: 100%;
    background: linear-gradient(
      105deg,
      transparent 30%,
      rgba(255,255,255,0.22) 50%,
      transparent 70%
    );
    transform: skewX(-15deg);
    transition: left 0.55s ease;
  }

  &:hover {
    filter: brightness(1.12);
    transform: translateY(-2px);
    &::before { left: 130%; }
  }

  &:active {
    transform: translateY(0);
    filter: brightness(0.95);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

// Main Layout
const DashboardLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

// Right Content Area
const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
  box-sizing: border-box;
`;

// Recommended Section
const RecommendedSection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.large};
  
  padding: ${theme.spacing.xl};
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: ${theme.spacing.lg};
  }
  
  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.medium};
  }
`;

const RecommendedTitle = styled.h2`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.lg};
  margin: 0 0 ${theme.spacing.lg} 0;
`;


const GameName = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.xs};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.xs};
  font-weight: ${theme.typography.fontWeights.semibold};
  text-align: center;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.3));
  line-height: 1.35;
  max-height: 72px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.fontSizes.xs};
    padding: ${theme.spacing.xs};
  }
`;

const GameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 160px));
  gap: ${theme.spacing.md};
  width: 100%;
  margin-bottom: 0;

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: ${theme.spacing.xs};
    gap: ${theme.spacing.md};

    & > * {
      flex: 0 0 140px;
      width: 140px;
    }
  }
`;

const FavoriteBrandsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${theme.spacing.xl};
  width: 100%;
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: 768px) {
    display: block;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: ${theme.spacing.xs};
    gap: ${theme.spacing.lg};
  }
`;

const BrandCardsStack = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: flex;
    flex-wrap: nowrap;
    gap: ${theme.spacing.lg};
    width: max-content;
  }
`;

const BrandCard = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3/4;
  background: rgba(20, 20, 35, 0);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(102, 126, 234, 0.3);
    border-color: ${theme.colors.primary};
  }

  @media (max-width: 768px) {
    flex: 0 0 170px;
    width: 170px;
  }
`;

const BrandCardImage = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.$src});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  }
`;

const BrandCardName = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  text-align: center;
  z-index: 2;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 1rem;
  }
`;

const GameCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 160px;
  aspect-ratio: 4/5;
  background: rgba(20, 20, 35, 0);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.35), 0 0 14px rgba(102, 126, 234, 0.2);
    border-color: ${theme.colors.primary};
    
    img {
      transform: scale(1.1);
    }
  }

  @media (max-width: 768px) {
    max-width: none;
    flex: 0 0 140px;
    width: 140px;
  }
`;

const GameIcon = styled.img`
  width: 100%;
  height: 64%;
  object-fit: cover;
  position: relative;
  
  @media (max-width: 768px) {
    height: 70%;
  }
  
  @media (max-width: 480px) {
    height: 70%;
  }
`;

const GameCardPrice = styled.div`
  color: ${theme.colors.accent};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeights.bold};
  margin-top: 2px;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

// Redeemable Offers
const OffersGrid = styled.div`

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const OfferCard = styled.div`
  background: transparent;
 
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.lg};
  display: flex;
  gap: ${theme.spacing.md};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;

const OfferIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: ${theme.borderRadius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  flex-shrink: 0;
`;

const OfferContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const OfferTitle = styled.h3`
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSizes.md};
  margin: 0;
`;

const OfferPoints = styled.div`
  color: ${theme.colors.primary};
  font-weight: bold;
  font-size: ${theme.typography.fontSizes.md};
`;

const OfferRedeemBtn = styled.button`
  display: inline-block;
  align-self: flex-start;
  background: ${theme.gradients.primary};
  color: #000;
  border: none;
  padding: 6px 16px;
  border-radius: ${theme.borderRadius.small};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.25s ease;
  margin-top: 6px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.button};
    opacity: 0.9;
  }
`;

// ── Stats Row ─────────────────────────────────────────────────
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.lg};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const StatCard = styled.div`
  position: relative;
  overflow: hidden;

  /* same warm-dark surface used throughout the app */
  background: rgba(10, 9, 5, 0.78);
  backdrop-filter: blur(18px);

  /* app-signature left gold bar + thin perimeter */
  border: 1px solid ${props => props.$isActive ? 'rgba(255,215,0,0.32)' : 'rgba(255,215,0,0.12)'};
  border-left: 3px solid ${props => props.$isActive ? 'rgba(255,215,0,0.85)' : 'rgba(255,215,0,0.38)'};

  /* app-signature cut-corner — same polygon as DashboardHeader cards */
  clip-path: polygon(
    0 0, 100% 0,
    100% calc(100% - 16px),
    calc(100% - 16px) 100%,
    0 100%
  );

  padding: ${theme.spacing.lg};
  box-sizing: border-box;
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  transition: border-color 0.22s ease, transform 0.22s ease;
  opacity: ${props => props.$isDisabled ? 0.38 : 1};

  /* dot-grid texture — mirrors DashboardHeader */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px);
    background-size: 22px 22px;
    pointer-events: none;
    z-index: 0;
  }

  /* shimmer sweep — same as ActionCard */
  &::after {
    content: '';
    position: absolute;
    top: -10%; left: -100%;
    width: 55%; height: 120%;
    background: linear-gradient(
      105deg,
      transparent 20%,
      rgba(255,215,0,0.06) 50%,
      transparent 80%
    );
    transform: skewX(-18deg);
    pointer-events: none;
  }

  ${props => props.$isActive && css`
    animation: ${spinCardFloat} 2.6s ease-in-out infinite;
    &::after { animation: ${spinCardShimmer} 3.2s ease-in-out infinite; }
  `}

  &:hover {
    transform: ${props => props.$isClickable ? 'translateY(-3px)' : 'none'};
    border-left-color: rgba(255,215,0,${props => props.$isActive ? '1' : '0.55'});
    border-color: rgba(255,215,0,${props => props.$isActive ? '0.45' : '0.22'});
  }

  > * { position: relative; z-index: 1; }

  @media (max-width: 480px) {
    padding: ${theme.spacing.md};
    clip-path: polygon(
      0 0, 100% 0,
      100% calc(100% - 10px),
      calc(100% - 10px) 100%,
      0 100%
    );
  }
`;

const StatCardScan = styled.div``; /* unused — kept for compat */

const StatCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

const StatCardTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  background: rgba(255,215,0,0.07);
  border: 1px solid rgba(255,215,0,0.22);
  border-radius: 4px;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255,215,0,0.75);
`;

const StatCardIndex = styled.div`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(255,255,255,0.18);
  font-variant-numeric: tabular-nums;
`;

const StatCardValue = styled.div`
  font-size: clamp(2.8rem, 5.5vw, 3.6rem);
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 0.88;
  color: ${props => props.$isSpinWord ? '#FFD700' : (props.$isActive ? '#FFD700' : '#ffffff')};
  transition: color 0.25s ease, background-position 0.25s ease;
  background: ${props => props.$isSpinWord ? 'linear-gradient(100deg, #ffffff 0%, #FFD700 55%, #FF8C00 100%)' : 'none'};
  background-size: ${props => props.$isSpinWord ? '200% auto' : 'auto'};
  -webkit-background-clip: ${props => props.$isSpinWord ? 'text' : 'initial'};
  -webkit-text-fill-color: ${props => props.$isSpinWord ? 'transparent' : 'initial'};
  background-clip: ${props => props.$isSpinWord ? 'text' : 'initial'};
  animation: ${props => props.$isSpinWord ? css`${spinWordGradientSlide} 4.5s linear infinite` : 'none'};
  margin-bottom: 5px;
  font-variant-numeric: tabular-nums;
`;

const StatCardSub = styled.div`
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.3);
  margin-bottom: ${theme.spacing.md};
`;

const StatCardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255,215,0,0.1);
`;

const SpinCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid rgba(255,215,0,0.1);
`;

const SpinCountdownBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,215,0,0.18);
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.45);
`;

const SpinCountdownFill = styled.div`
  height: 100%;
  width: ${props => props.$fill || '0%'};
  min-width: ${props => props.$fill && parseFloat(props.$fill) > 0 ? '8px' : '0'};
  background: linear-gradient(90deg, rgba(255,215,0,0.95), rgba(255,140,0,0.85));
  border-radius: 999px;
  box-shadow: 0 0 8px rgba(255,215,0,0.45);
  transition: width 0.6s ease;
`;

const StatCardStatus = styled.div`
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${props => props.$active ? 'rgba(255,215,0,0.75)' : 'rgba(255,255,255,0.25)'};
  white-space: nowrap;
  flex-shrink: 0;
`;

const StatCardBar = styled.div`
  flex: 1;
  height: 2px;
  background: rgba(255,255,255,0.06);
  border-radius: 1px;
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.$fill || '0%'};
    background: linear-gradient(90deg, rgba(255,215,0,0.65), rgba(255,140,0,0.45));
    border-radius: 1px;
    transition: width 0.6s ease;
  }
`;

/* kept for backward compat */
const StatCardBody = styled.div``;
const StatCardCategory = styled.div``;
const StatCardTitle = styled.div``;
const StatCardMeta = styled.div``;
const StatCardCta = styled.span``;
const StatCardIconBox = styled.div``;
const StatValue = styled.div``;
const StatLabel = styled.div``;

const normalizeValue = (value) => (value || '').toString().trim().toLowerCase();
const normalizeRegion = (value) => (value || '').toString().trim().toLowerCase();
const getCountryNameFromCode = (code) => {
  if (!code) return '';
  if (typeof code === 'string' && code.length > 2) return code;
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return displayNames.of(code) || '';
  } catch (err) {
    return '';
  }
};
const getUserCountryTokens = (userData) => {
  const rawCountry = userData?.metadata?.country || userData?.country || userData?.countryCode || userData?.region || '';
  const countryName = getCountryNameFromCode(rawCountry);
  return [countryName, rawCountry]
    .filter(Boolean)
    .map((value) => normalizeRegion(value));
};
const isGlobalRegion = (product) => {
  const regionName = normalizeRegion(product?.regionName || product?.region || product?.country || '');
  return ['global', 'international', 'worldwide', 'all regions', 'all'].some((token) => regionName.includes(token));
};
const isPreferredRegion = (product, preferredTokens) => {
  if (!preferredTokens.length) return false;
  const regionName = normalizeRegion(product?.regionName || product?.region || product?.country || '');
  return preferredTokens.some((token) => regionName.includes(token));
};
const pickBestProduct = (products) => {
  if (!products.length) return null;
  return [...products].sort((a, b) => (b.available || 0) - (a.available || 0))[0];
};

const Dashboard = ({ onNavigate }) => {
  const { user } = useAuth();
  // Local mirror of user.points; updated on auth context changes for real-time navbar consistency
  const [userPoints, setUserPoints] = useState(user?.points || 0);
  const [topOffers, setTopOffers] = useState([]);
  const [isLoadingOffers, setIsLoadingOffers] = useState(true);
  const [canSpin, setCanSpin] = useState(false);
  const [spinRemaining, setSpinRemaining] = useState(0);
  const [spinTotalFree, setSpinTotalFree] = useState(0);
  const [spinLastResetAt, setSpinLastResetAt] = useState(null);
  const [spinNextResetAt, setSpinNextResetAt] = useState(null);
  const [viewerNowMs, setViewerNowMs] = useState(Date.now());
  const [showOffers, setShowOffers] = useState(true);
  const [favoriteBrands, setFavoriteBrands] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);

  // Read latest user cosmetics data from localStorage
  const [userAvatarUrl, setUserAvatarUrl] = useState(user?.avatarUrl || null);
  const [userMeta, setUserMeta] = useState(user?.metadata || {});

  useEffect(() => {
    setUserAvatarUrl(user?.avatarUrl || null);
    setUserMeta(user?.metadata || {});
    try {
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      if (stored.avatarUrl) setUserAvatarUrl(stored.avatarUrl);
      if (stored.metadata) setUserMeta(stored.metadata);
    } catch {}
  }, [user]);

  // Sync points from auth context
  useEffect(() => {
    if (user?.points != null) {
      setUserPoints(user.points);
    }
  }, [user?.points]);

  const formatSpinCountdown = (milliseconds) => {
    const safeMs = Math.max(0, Number(milliseconds || 0));
    const totalSeconds = Math.floor(safeMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const getViewerCountdownMs = () => {
    if (!spinNextResetAt) return 0;
    const nextResetMs = new Date(spinNextResetAt).getTime();
    if (Number.isNaN(nextResetMs)) return 0;
    return Math.max(0, nextResetMs - viewerNowMs);
  };

  const getSpinResetProgressPercent = () => {
    if (canSpin) return 100;
    if (!spinLastResetAt || !spinNextResetAt) return 0;

    const lastResetMs = new Date(spinLastResetAt).getTime();
    const nextResetMs = new Date(spinNextResetAt).getTime();
    if (Number.isNaN(lastResetMs) || Number.isNaN(nextResetMs) || nextResetMs <= lastResetMs) return 0;

    const totalWindowMs = nextResetMs - lastResetMs;
    const remainingMs = Math.max(0, nextResetMs - viewerNowMs);
    const elapsedMs = totalWindowMs - remainingMs;
    const percent = (elapsedMs / totalWindowMs) * 100;
    return Math.max(0, Math.min(100, percent));
  };

  useEffect(() => {
    let isMounted = true;

    const fetchSpinAvailability = async () => {
      if (!user) return;
      try {
        const SpinWheelService = (await import('../../services/SpinWheelService')).default;
        const data = await SpinWheelService.getWheelConfig();
        if (!isMounted || !data?.success) return;

        const remaining = Number(data?.userData?.remainingSpins || 0);
        const totalFree = Number(data?.config?.dailyFreeSpins || 0);
        const isEligible = data?.userData?.canUseFreeSpin === true || remaining > 0;

        setSpinRemaining(remaining);
        setSpinTotalFree(totalFree);
        setSpinLastResetAt(data?.userData?.lastResetTime || null);
        setSpinNextResetAt(data?.userData?.nextResetTime || null);
        setCanSpin(isEligible);
      } catch (error) {
        console.error('[Dashboard] Failed to fetch spin availability:', error);
      }
    };

    fetchSpinAvailability();
    const pollInterval = setInterval(fetchSpinAvailability, 60000);

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
    };
  }, [user]);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setViewerNowMs(Date.now());
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, []);

  const spinResetProgressPercent = getSpinResetProgressPercent();

  // Fetch top offers from favorite brands
  useEffect(() => {
    const fetchTopOffersFromFavorites = async () => {
      try {
        console.log('[Dashboard] fetchTopOffersFromFavorites triggered');
        setIsLoadingOffers(true);
        if (!user) {
          console.log('[Dashboard] Skipping fetchTopOffers - no user');
          return;
        }

        // Import services dynamically
        const [ApiService, ProductService] = await Promise.all([
          import('../../services/ApiService').then(m => m.default),
          import('../../services/ProductService').then(m => m.default)
        ]);
        
        // First get favorite brands
        const userId = user.id || user.documentId;
        if (!userId) {
          console.warn('User ID not found for fetching favorites');
          return;
        }
        const favorites = await ApiService.getFavoriteBrands(userId);
        
        if (favorites.length === 0) {
          // If no favorites, show top 3 products from any brand
          const response = await ProductService.getCatalog({
            page: 1,
            limit: 3,
            sortBy: 'name'
          });
          if (response?.success && response.data?.products) {
            setTopOffers(response.data.products.slice(0, 3));
          }
          return;
        }

        const normalizeBrand = normalizeValue;
        const extractBrandFromProduct = (product) => {
          const productName = (product?.productName || product?.title || product?.displayName || product?.merchantName || '').trim();
          const brandPatterns = [
            /^(Apple)/i,
            /^(Google Play|Google)/i,
            /^(Amazon)/i,
            /^(Xbox)/i,
            /^(PlayStation|PS\d?)/i,
            /^(Steam)/i,
            /^(Netflix)/i,
            /^(Spotify)/i,
            /^(Samsung)/i,
            /^(Nintendo)/i,
            /^(Visa)/i,
            /^(Mastercard)/i,
            /^(Epic Games)/i,
            /^(Battle\.net|Blizzard)/i,
            /^(Riot Games)/i,
            /^(Discord)/i,
            /^(Twitch)/i,
            /^(EA|Origin)/i,
            /^(Ubisoft)/i,
            /^(Rockstar)/i,
            /^(GOG)/i,
            /^(Disney)/i,
            /^(Hulu)/i,
            /^(Crunchyroll)/i,
            /^(YouTube)/i,
            /^(Razer)/i,
            /^(Humble)/i,
            /^(Walmart)/i,
            /^(Target)/i,
            /^(Best Buy)/i,
            /^(eBay)/i,
            /^(Uber)/i,
            /^(DoorDash)/i,
            /^(Starbucks)/i,
            /^(Chipotle)/i,
            /(PUBG|PlayerUnknown'?s?\s*Battlegrounds?)/i,
            /(Roblox)/i,
            /(Minecraft)/i,
            /(Fortnite)/i,
            /(Call of Duty|COD)/i,
            /(Free Fire)/i,
            /(Mobile Legends|MLBB)/i,
            /(Genshin Impact)/i,
            /(League of Legends|LoL)/i,
            /(Valorant)/i,
            /(Apex Legends)/i,
            /(Overwatch)/i,
            /(Counter-Strike|CS:?GO|CS2)/i,
            /(Dota\s*2)/i,
            /(Grand Theft Auto|GTA)/i,
            /(World of Warcraft|WoW)/i,
            /(Final Fantasy|FF\s*\d+)/i,
            /(Pokemon|Pokémon)/i,
            /(Candy Crush)/i,
            /(Clash of Clans|Clash Royale)/i,
            /(Among Us)/i,
            /(Fall Guys)/i,
            /(Rocket League)/i,
            /(Rainbow Six)/i,
            /(Destiny\s*\d?)/i,
            /(The Sims)/i,
            /(Red Dead)/i,
            /(FIFA)/i,
            /(NBA\s*2K)/i,
            /(Madden)/i,
            /^([A-Z][a-z]+)/
          ];

          for (const pattern of brandPatterns) {
            const match = productName.match(pattern);
            if (match) {
              return match[1] || match[0];
            }
          }
          return product?.brandName || product?.merchantName || productName.split(' ')[0] || '';
        };

        // Fetch all products to filter by favorite brands
        const initialResponse = await ProductService.getCatalog({
          page: 1,
          limit: 1000,
          sortBy: 'name'
        });

        if (initialResponse?.success && initialResponse.data?.products) {
          let allProducts = initialResponse.data.products;
          const totalPagesToFetch = initialResponse.data.pagination?.totalPages || 1;

          if (totalPagesToFetch > 1) {
            const pageRequests = [];
            for (let page = 2; page <= totalPagesToFetch; page += 1) {
              pageRequests.push(ProductService.getCatalog({
                page,
                limit: 1000,
                sortBy: 'name'
              }));
            }

            const pageResponses = await Promise.all(pageRequests);
            pageResponses.forEach((pageResponse) => {
              if (pageResponse?.success && pageResponse.data?.products) {
                allProducts = allProducts.concat(pageResponse.data.products);
              }
            });
          }

          const offersFromFavorites = [];
          const preferredRegionTokens = getUserCountryTokens(user);

          // Extract brand names from favorites
          const favoriteBrandNames = favorites.map(fav => 
            fav.attributes?.brandName || fav.brandName
          ).filter(Boolean);

          // For each favorite brand, find the best available product
          favoriteBrandNames.forEach((brandName) => {
            const normalizedFavorite = normalizeBrand(brandName);
            const matchingProducts = allProducts.filter((product) => {
              const productBrandName = normalizeBrand(product.brandName || extractBrandFromProduct(product));
              return productBrandName === normalizedFavorite;
            });

            const availableMatches = matchingProducts.filter((product) => (product.available || 0) > 0);
            const preferredRegionMatches = availableMatches.filter((product) => isPreferredRegion(product, preferredRegionTokens));
            const globalMatches = availableMatches.filter((product) => isGlobalRegion(product));
            const pickList =
              preferredRegionMatches.length > 0 ? preferredRegionMatches
              : globalMatches.length > 0 ? globalMatches
              : availableMatches.length > 0 ? availableMatches
              : matchingProducts;

            const bestMatch = pickBestProduct(pickList);
            if (bestMatch) {
              offersFromFavorites.push(bestMatch);
            }
          });

          // Set the offers (up to 3 from favorite brands)
          setTopOffers(offersFromFavorites.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching top offers from favorites:', error);
      } finally {
        setIsLoadingOffers(false);
      }
    };

    if (user) {
      fetchTopOffersFromFavorites();
    }
  }, [user]);

  useEffect(() => {
    const fetchFavoriteBrands = async () => {
      try {
        console.log('[Dashboard] fetchFavoriteBrands triggered');
        setLoadingFavorites(true);
        if (!user) {
          console.log('[Dashboard] Skipping fetchFavoriteBrands - no user');
          return;
        }

        const ApiService = (await import('../../services/ApiService')).default;
        const userId = user.id || user.documentId;
        if (!userId) return;
        
        const favorites = await ApiService.getFavoriteBrands(userId);
        setFavoriteBrands(favorites);
      } catch (error) {
        console.error('Error fetching favorite brands:', error);
      } finally {
        setLoadingFavorites(false);
      }
    };

    if (user) {
      fetchFavoriteBrands();
    }
  }, [user]);

  // Fetch recommended products based on favorites
  useEffect(() => {
    const fetchRecommendedProducts = async () => {
      try {
        console.log('[Dashboard] fetchRecommendedProducts triggered');
        setLoadingRecommended(true);
        if (!user) {
          console.log('[Dashboard] Skipping fetchRecommended - no user');
          return;
        }

        const [ApiService, ProductService] = await Promise.all([
          import('../../services/ApiService').then(m => m.default),
          import('../../services/ProductService').then(m => m.default)
        ]);

        const parseClickedStoreItems = (userData) => {
          const raw = userData?.clickedStoreItems;
          if (!raw) return {};
          if (typeof raw === 'string') {
            try {
              return JSON.parse(raw);
            } catch (err) {
              console.warn('[Dashboard] Failed to parse clickedStoreItems', err);
              return {};
            }
          }
          return raw;
        };
        const extractBrandFromProduct = (product) => {
          const productName = (product?.productName || product?.title || product?.displayName || product?.merchantName || '').trim();
          const brandPatterns = [
            /^(Apple)/i,
            /^(Google Play|Google)/i,
            /^(Amazon)/i,
            /^(Xbox)/i,
            /^(PlayStation|PS\d?)/i,
            /^(Steam)/i,
            /^(Netflix)/i,
            /^(Spotify)/i,
            /^(Samsung)/i,
            /^(Nintendo)/i,
            /^(Visa)/i,
            /^(Mastercard)/i,
            /^(Epic Games)/i,
            /^(Battle\.net|Blizzard)/i,
            /^(Riot Games)/i,
            /^(Discord)/i,
            /^(Twitch)/i,
            /^(EA|Origin)/i,
            /^(Ubisoft)/i,
            /^(Rockstar)/i,
            /^(GOG)/i,
            /^(Disney)/i,
            /^(Hulu)/i,
            /^(Crunchyroll)/i,
            /^(YouTube)/i,
            /^(Razer)/i,
            /^(Humble)/i,
            /^(Walmart)/i,
            /^(Target)/i,
            /^(Best Buy)/i,
            /^(eBay)/i,
            /^(Uber)/i,
            /^(DoorDash)/i,
            /^(Starbucks)/i,
            /^(Chipotle)/i,
            /(PUBG|PlayerUnknown'?s?\s*Battlegrounds?)/i,
            /(Roblox)/i,
            /(Minecraft)/i,
            /(Fortnite)/i,
            /(Call of Duty|COD)/i,
            /(Free Fire)/i,
            /(Mobile Legends|MLBB)/i,
            /(Genshin Impact)/i,
            /(League of Legends|LoL)/i,
            /(Valorant)/i,
            /(Apex Legends)/i,
            /(Overwatch)/i,
            /(Counter-Strike|CS:?GO|CS2)/i,
            /(Dota\s*2)/i,
            /(Grand Theft Auto|GTA)/i,
            /(World of Warcraft|WoW)/i,
            /(Final Fantasy|FF\s*\d+)/i,
            /(Pokemon|Pokémon)/i,
            /(Candy Crush)/i,
            /(Clash of Clans|Clash Royale)/i,
            /(Among Us)/i,
            /(Fall Guys)/i,
            /(Rocket League)/i,
            /(Rainbow Six)/i,
            /(Destiny\s*\d?)/i,
            /(The Sims)/i,
            /(Red Dead)/i,
            /(FIFA)/i,
            /(NBA\s*2K)/i,
            /(Madden)/i,
            /^([A-Z][a-z]+)/
          ];

          for (const pattern of brandPatterns) {
            const match = productName.match(pattern);
            if (match) {
              return match[1] || match[0];
            }
          }
          return product?.brandName || product?.merchantName || productName.split(' ')[0] || '';
        };
        const getTopClickedBrands = (clickedItems) => {
          return Object.entries(clickedItems)
            .filter(([brand]) => brand && brand.toLowerCase() !== 'other' && brand.toLowerCase() !== 'test')
            .map(([brand, data]) => ({
              brand,
              clicks: data?.clicks || 0,
              regions: Object.entries(data || {}).filter(([key]) => key !== 'clicks')
            }))
            .sort((a, b) => b.clicks - a.clicks);
        };
        const getTopRegionForBrand = (brandData) => {
          const regionEntries = brandData.regions
            .map(([regionName, regionData]) => ({
              regionName,
              clicks: regionData?.clicks || 0,
              items: Object.entries(regionData || {}).filter(([key]) => key !== 'clicks')
            }))
            .sort((a, b) => b.clicks - a.clicks);

          return regionEntries[0] || null;
        };
        const findProductByRef = (products, productRef) => products.find(product => product.id === productRef);
        const collectTopProductRefs = (brandData) => {
          const productRefs = [];
          brandData.regions.forEach(([regionName, regionData]) => {
            Object.entries(regionData || {})
              .filter(([key]) => key !== 'clicks')
              .sort(([, a], [, b]) => (b?.clicks || 0) - (a?.clicks || 0))
              .forEach(([productRef]) => productRefs.push(productRef));
          });
          return productRefs;
        };

        const clickedStoreItems = parseClickedStoreItems(user);
        const preferredRegionTokens = getUserCountryTokens(user);
        
        // Fetch available products
        const initialResponse = await ProductService.getCatalog({
          page: 1,
          limit: 1000,
          sortBy: 'name'
        });

        if (initialResponse?.success && initialResponse.data?.products) {
          let allProducts = initialResponse.data.products;
          const totalPagesToFetch = initialResponse.data.pagination?.totalPages || 1;

          if (totalPagesToFetch > 1) {
            const pageRequests = [];
            for (let page = 2; page <= totalPagesToFetch; page += 1) {
              pageRequests.push(ProductService.getCatalog({
                page,
                limit: 1000,
                sortBy: 'name'
              }));
            }

            const pageResponses = await Promise.all(pageRequests);
            pageResponses.forEach((pageResponse) => {
              if (pageResponse?.success && pageResponse.data?.products) {
                allProducts = allProducts.concat(pageResponse.data.products);
              }
            });
          }
          // Show all products, not just available ones - let users see everything
          const availableProducts = allProducts; // Changed from filter

          let recommended = [];

          const clickedBrands = getTopClickedBrands(clickedStoreItems);
          if (clickedBrands.length > 0) {
            const selectedBrands = new Set();
            for (const brandData of clickedBrands) {
              if (recommended.length >= 3) break;

              const normalizedBrand = normalizeValue(brandData.brand);
              const brandProducts = availableProducts.filter((product) => {
                const productBrand = normalizeValue(product.brandName || extractBrandFromProduct(product));
                return productBrand === normalizedBrand;
              });

              if (!brandProducts.length || selectedBrands.has(normalizedBrand)) {
                continue;
              }

              const brandRegion = getTopRegionForBrand(brandData);
              const topProductRefCandidates = collectTopProductRefs(brandData);
              const productRefMatch = topProductRefCandidates
                .map((productRef) => findProductByRef(brandProducts, productRef))
                .find(Boolean);

              let candidateList = brandProducts;
              if (productRefMatch) {
                candidateList = [productRefMatch];
              } else if (brandRegion) {
                const regionToken = normalizeRegion(brandRegion.regionName);
                const regionMatches = brandProducts.filter((product) => {
                  const regionName = normalizeRegion(product?.regionName || product?.region || product?.country || '');
                  return regionName.includes(regionToken);
                });

                if (regionMatches.length > 0) {
                  candidateList = regionMatches;
                }
              }

              const availableMatches = candidateList.filter((product) => (product.available || 0) > 0);
              const preferredRegionMatches = availableMatches.filter((product) => isPreferredRegion(product, preferredRegionTokens));
              const globalMatches = availableMatches.filter((product) => isGlobalRegion(product));
              const pickList =
                preferredRegionMatches.length > 0 ? preferredRegionMatches
                : globalMatches.length > 0 ? globalMatches
                : availableMatches.length > 0 ? availableMatches
                : candidateList;

              const bestMatch = pickBestProduct(pickList);
              if (bestMatch) {
                recommended.push(bestMatch);
                selectedBrands.add(normalizedBrand);
              }
            }
          }

          // Enhanced recommendation algorithm
          const getProductMetadata = (product) => {
            const name = (product.productName || product.title || '').toLowerCase();
            const words = name.split(/[\s-]+/);
            return {
              brand: words[0] || '',
              platform: name.match(/\b(playstation|xbox|steam|nintendo|pc|mobile)\b/i)?.[0]?.toLowerCase() || '',
              type: name.match(/\b(gift card|code|subscription|credit|points|membership)\b/i)?.[0]?.toLowerCase() || 'gift card',
              category: name.match(/\b(gaming|game|digital|entertainment)\b/i)?.[0]?.toLowerCase() || 'gaming',
              value: product.sellingPrice || 0,
              name: product.productName || product.title,
              fullName: name
            };
          };

          // Calculate relevance score based on user's favorite brands
          const calculateRelevanceScore = (product, favoriteBrandNames, userPreferences) => {
            let score = 0;
            const metadata = getProductMetadata(product);
            
            // Penalty for duplicate brands shown in offers (use current state snapshot)
            const currentTopOffers = topOffers || [];
            const shownRefs = currentTopOffers.map(o => o.id || '');
            if (shownRefs.includes(product.id)) return -1000;
            
            // Check if from favorite brand (we want different brands, so negative score)
            const isFromFavoriteBrand = favoriteBrandNames.some(brandName => 
              metadata.fullName.includes(brandName) || brandName.includes(metadata.brand)
            );
            if (isFromFavoriteBrand) return -500; // Deprioritize favorite brands to show variety
            
            // Prefer similar platform/ecosystem to favorites
            if (userPreferences.platforms.includes(metadata.platform) && metadata.platform) {
              score += 50;
            }
            
            // Prefer similar price range (affordable alternatives)
            const avgFavoritePrice = userPreferences.avgPrice;
            const priceDiff = Math.abs(metadata.value - avgFavoritePrice);
            const priceRatio = priceDiff / avgFavoritePrice;
            if (priceRatio < 0.3) score += 30; // Within 30% price range
            else if (priceRatio < 0.5) score += 20; // Within 50% price range
            else if (priceRatio < 1) score += 10; // Within 100% price range
            
            // Boost popular gaming brands
            const popularBrands = ['steam', 'playstation', 'xbox', 'nintendo', 'roblox', 'fortnite', 'valorant', 'amazon', 'google'];
            if (popularBrands.includes(metadata.brand)) score += 25;
            
            // Boost products with good images
            if (getProductImageUrl(product)) score += 15;
            
            // Boost based on availability (higher stock = more popular)
            if (product.available > 50) score += 10;
            else if (product.available > 20) score += 5;
            
            // Random variety bonus to keep it interesting
            score += Math.random() * 15;
            
            return score;
          };

          if (recommended.length === 0 && favoriteBrands.length > 0) {
            // Extract user preferences from favorite brands
            const favoriteBrandNames = favoriteBrands.map(fav => 
              (fav.attributes?.brandName || fav.brandName).toLowerCase()
            );
            
            const userPreferences = {
              platforms: [],
              avgPrice: 0,
              brands: favoriteBrandNames
            };
            
            // Analyze favorite brands to extract platform preferences
            const currentTopOffers = topOffers || [];
            currentTopOffers.forEach(offer => {
              const metadata = getProductMetadata(offer);
              if (metadata.platform) userPreferences.platforms.push(metadata.platform);
              userPreferences.avgPrice += metadata.value;
            });
            userPreferences.avgPrice = userPreferences.avgPrice / Math.max(currentTopOffers.length, 1) || 1000;
            
            // Score all eligible products
            const scoredProducts = availableProducts
              .map(product => ({
                product,
                score: calculateRelevanceScore(product, favoriteBrandNames, userPreferences),
                metadata: getProductMetadata(product)
              }))
              .filter(item => item.score > 0) // Filter out irrelevant products
              .sort((a, b) => b.score - a.score); // Sort by relevance
            
            // Ensure brand diversity - select top 3 from different brands
            const selectedBrands = new Set();
            const selectedPlatforms = new Set();
            
            for (const item of scoredProducts) {
              if (recommended.length >= 3) break;
              
              const brandKey = item.metadata.brand;
              const platformKey = item.metadata.platform || 'other';
              
              // Skip if we already have this brand or prefer platform diversity
              if (selectedBrands.has(brandKey)) continue;
              
              // Prefer platform diversity (don't show 3 Steam cards, for example)
              if (recommended.length > 0 && selectedPlatforms.size < 2 && selectedPlatforms.has(platformKey)) {
                continue;
              }
              
              recommended.push(item.product);
              selectedBrands.add(brandKey);
              if (platformKey !== 'other') selectedPlatforms.add(platformKey);
            }

            // If still not enough, add highest scored remaining products
            if (recommended.length < 3) {
              const remainingProducts = scoredProducts.filter(item => 
                !recommended.some(rec => rec.id === item.product.id)
              );
              recommended = [...recommended, ...remainingProducts.slice(0, 3 - recommended.length).map(item => item.product)];
            }
          } else if (recommended.length === 0) {
            // No favorites - show trending/popular products from diverse categories
            const validProducts = availableProducts.filter(p => {
              const name = (p.productName || p.title || '').trim();
              return name && getProductImageUrl(p) && p.sellingPrice > 0;
            });
            
            // Group by brand and select popular ones
            const productsByBrand = {};
            validProducts.forEach(product => {
              const metadata = getProductMetadata(product);
              const brandKey = metadata.brand;
              
              if (!productsByBrand[brandKey]) {
                productsByBrand[brandKey] = [];
              }
              productsByBrand[brandKey].push({
                product,
                score: (product.available || 0) + Math.random() * 50 // Popularity + randomness
              });
            });
            
            // Sort brands by total popularity
            const brandNames = Object.keys(productsByBrand)
              .map(brand => ({
                brand,
                popularity: productsByBrand[brand].reduce((sum, item) => sum + item.score, 0)
              }))
              .sort((a, b) => b.popularity - a.popularity)
              .map(item => item.brand);
            
            // Select best product from top 3 brands
            for (const brandName of brandNames) {
              if (recommended.length >= 3) break;
              const brandProducts = productsByBrand[brandName].sort((a, b) => b.score - a.score);
              recommended.push(brandProducts[0].product);
            }
          }

          setRecommendedProducts(recommended);
        }
      } catch (error) {
        console.error('Error fetching recommended products:', error);
      } finally {
        setLoadingRecommended(false);
      }
    };

    if (user) {
      fetchRecommendedProducts();
    }
  }, [user, favoriteBrands, topOffers]);

  const quickActions = [
    { id: 'scanner',   icon: <ScanIcon />,      title: 'Scan QR Code'   },
    { id: 'points',    icon: <BalanceIcon />,   title: 'View GG Coins'  },
    { id: 'catalog',   icon: <RedeemIcon />,    title: 'Browse Rewards' },
    { id: 'inventory', icon: <InventoryIcon />, title: 'Open Inventory' },
  ];

  const handleActionClick = (actionId) => {
    if (onNavigate) {
      onNavigate(actionId);
    }
  };

  const handleOfferRedeem = (offer) => {
    const offerRegionTokens = [offer?.regionName, offer?.region, offer?.country]
      .filter(Boolean);
    const userRegionTokens = getUserCountryTokens(user);
    const preferredTokens = Array.from(new Set([...offerRegionTokens, ...userRegionTokens]));
    if (preferredTokens.length > 0) {
      sessionStorage.setItem('preferredRegionTokens', JSON.stringify(preferredTokens));
    }
    // Store the selected product in sessionStorage for catalog to pick up
    sessionStorage.setItem('selectedProduct', JSON.stringify(offer));
    if (onNavigate) {
      onNavigate('catalog');
    }
  };

  const handleOfferCardClick = (offer) => {
    const offerRegionTokens = [offer?.regionName, offer?.region, offer?.country]
      .filter(Boolean);
    const userRegionTokens = getUserCountryTokens(user);
    const preferredTokens = Array.from(new Set([...offerRegionTokens, ...userRegionTokens]));
    if (preferredTokens.length > 0) {
      sessionStorage.setItem('preferredRegionTokens', JSON.stringify(preferredTokens));
    }
    // Navigate to catalog and show this specific product
    sessionStorage.setItem('viewProduct', JSON.stringify(offer));
    if (onNavigate) {
      onNavigate('catalog');
    }
  };

  const handleFavoriteBrandClick = (brandName) => {
    const userRegionTokens = getUserCountryTokens(user);
    if (userRegionTokens.length > 0) {
      sessionStorage.setItem('preferredRegionTokens', JSON.stringify(userRegionTokens));
    }
    // Store brand filter for catalog
    sessionStorage.setItem('filterByBrand', brandName);
    if (onNavigate) {
      onNavigate('catalog');
    }
  };

  return (
    <DashboardContainer>
      {/* ── Meteor shower layer ── */}
      <MeteorLayer aria-hidden="true">
        {/* cluster 1 — top region */}
        <Meteor $anim={meteorA} $dur="13s"  $delay="0s"    $top="8%"  $left="18%"  $h="280px" />
        <Meteor $anim={meteorB} $dur="17s"  $delay="4.5s"  $top="5%"  $left="72%"  $h="220px" $c1="rgba(255,200,0,0.22)" $c2="rgba(255,110,0,0.14)" />
        <Meteor $anim={meteorC} $dur="11s"  $delay="2.2s"  $top="12%" $left="45%"  $h="180px" $c1="rgba(255,220,0,0.3)"  $c2="rgba(255,90,0,0.18)" />
        {/* cluster 2 — mid-left */}
        <Meteor $anim={meteorD} $dur="16s"  $delay="7s"    $top="35%" $left="5%"   $h="320px" />
        <Meteor $anim={meteorA} $dur="20s"  $delay="1.8s"  $top="42%" $left="22%"  $h="150px" $c1="rgba(255,215,0,0.18)" $c2="rgba(255,140,0,0.1)" />
        <Meteor $anim={meteorE} $dur="14s"  $delay="9.5s"  $top="28%" $left="38%"  $h="250px" $c1="rgba(255,210,0,0.26)" $c2="rgba(255,120,0,0.16)" />
        {/* cluster 3 — mid-right */}
        <Meteor $anim={meteorB} $dur="18s"  $delay="3s"    $top="30%" $left="78%"  $h="300px" />
        <Meteor $anim={meteorC} $dur="12s"  $delay="11s"   $top="50%" $left="90%"  $h="200px" $c1="rgba(255,200,0,0.24)" $c2="rgba(255,100,0,0.15)" />
        <Meteor $anim={meteorD} $dur="15s"  $delay="6.3s"  $top="55%" $left="60%"  $h="260px" $c1="rgba(255,220,0,0.2)"  $c2="rgba(255,130,0,0.12)" />
        {/* cluster 4 — lower region */}
        <Meteor $anim={meteorA} $dur="19s"  $delay="13s"   $top="72%" $left="30%"  $h="190px" $c1="rgba(255,215,0,0.22)" $c2="rgba(255,140,0,0.13)" />
        <Meteor $anim={meteorE} $dur="22s"  $delay="5.7s"  $top="80%" $left="55%"  $h="340px" />
        <Meteor $anim={meteorB} $dur="10s"  $delay="15.5s" $top="65%" $left="85%"  $h="170px" $c1="rgba(255,210,0,0.3)"  $c2="rgba(255,100,0,0.18)" />
      </MeteorLayer>

      <PWAInstallBanner position="top" />
      <BannerCarousel />
      <DashboardContent>
        <DashboardHeader>
          <HeaderTop>
            <HeaderBranding>
              <HeaderLogo />
              <HeaderTitleGroup>
                <HeaderBadge>Gaming Rewards</HeaderBadge>
                <HeaderTitle>GG Trophy</HeaderTitle>
                <HeaderSubtitle>Scan · Earn · Redeem</HeaderSubtitle>
              </HeaderTitleGroup>
            </HeaderBranding>
            <HeaderActions>
              <UserGreeting onClick={() => onNavigate('profile')} style={{ cursor: 'pointer' }}>
                <DashUserAvatarWrapper>
                  <DashUserAvatar
                    $hasImage={!!userAvatarUrl}
                    $frameColor={userMeta.frameColor}
                  >
                    {userAvatarUrl
                      ? <DashUserAvatarImg
                          src={resolveAvatarUrl(userAvatarUrl)}
                          alt={user?.username}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.textContent = user?.username?.substring(0, 2).toUpperCase() || '??';
                          }}
                        />
                      : (user?.username?.substring(0, 2).toUpperCase() || '??')
                    }
                  </DashUserAvatar>
                  {userMeta.badgeImageUrl && (
                    <DashUserBadge>
                      <img
                        src={resolveAvatarUrl(userMeta.badgeImageUrl)}
                        alt={userMeta.badgeName || 'Badge'}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                    </DashUserBadge>
                  )}
                </DashUserAvatarWrapper>
                {user?.username}
              </UserGreeting>
              <RedeemButton onClick={() => handleActionClick('catalog')}>
                Redeem Rewards
              </RedeemButton>
            </HeaderActions>
          </HeaderTop>
        </DashboardHeader>

        <DashboardLayout>
          <ContentArea>
            <StatsRow>
              {/* ── Spin Card ── */}
              <StatCard
                $isActive={canSpin}
                $isClickable={true}
                $isDisabled={false}
                onClick={() => onNavigate('spin-wheel')}
              >
                <StatCardHeader>
                  <StatCardTag>Daily Mission</StatCardTag>
                  <StatCardIndex>{spinRemaining} / {spinTotalFree || 0}</StatCardIndex>
                </StatCardHeader>
                <StatCardValue $isActive={canSpin} $isSpinWord={true}>SPIN</StatCardValue>
                <StatCardSub>free spins</StatCardSub>
                <SpinCardFooter>
                  <StatCardStatus
                    $active={canSpin}
                    title={spinLastResetAt ? `Last reset: ${new Date(spinLastResetAt).toLocaleString()}` : 'Last reset: --'}
                  >
                    {canSpin ? 'Ready' : `Resets in ${formatSpinCountdown(getViewerCountdownMs())}`}
                  </StatCardStatus>
                  <SpinCountdownBar>
                    <SpinCountdownFill $fill={`${spinResetProgressPercent}%`} />
                  </SpinCountdownBar>
                </SpinCardFooter>
              </StatCard>

              {/* ── Rewards Card ── */}
              <StatCard
                $isClickable={topOffers.length > 0}
                onClick={() => topOffers.length > 0 && setShowOffers(!showOffers)}
                style={{ cursor: topOffers.length > 0 ? 'pointer' : 'default' }}
              >
                <StatCardHeader>
                  <StatCardTag>Rewards Vault</StatCardTag>
                  <StatCardIndex>02 / 02</StatCardIndex>
                </StatCardHeader>
                <StatCardValue>{topOffers.length}</StatCardValue>
                <StatCardSub>{topOffers.length === 1 ? 'offer available' : 'offers available'}</StatCardSub>
                <SpinCardFooter>
                  <StatCardStatus $active={topOffers.length > 0}>
                    {topOffers.length > 0
                      ? (showOffers ? 'Viewing' : 'Unlocked')
                      : 'Standby'}
                  </StatCardStatus>
                  <StatCardBar $fill={topOffers.length > 0 ? '100%' : '0%'} />
                </SpinCardFooter>
              </StatCard>
            </StatsRow>

            {showOffers && topOffers.length > 0 && (
              <RecommendedSection>
                <RecommendedTitle>
                  {favoriteBrands.length > 0 ? 'Top Offers from Your Favorite Brands' : 'Top Redeemable Offers'}
                </RecommendedTitle>
                {favoriteBrands.length > 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    color: theme.colors.textMuted, 
                    fontSize: '0.9rem',
                    marginBottom: '1rem'
                  }}>
                    Showing best offers from: {favoriteBrands.map(fav => fav.attributes?.brandName || fav.brandName).join(', ')}
                  </div>
                )}
                {isLoadingOffers ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>
                    Loading offers...
                  </div>
                ) : (
                  <OffersGrid>
                    {topOffers.map((offer) => {
                      const offerImage = getProductImageUrl(offer);
                      const offerFallback = buildInlinePlaceholder(offer.productName || offer.title || 'Gift Card');
                      return (
                        <OfferCard 
                          key={offer.id}
                          onClick={() => handleOfferCardClick(offer)}
                          style={{ cursor: 'pointer' }}
                        >
                          <OfferIcon>
                            {offerImage ? (
                              <img 
                                src={offerImage} 
                                alt={offer.productName || offer.title}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                                onError={(e) => {
                                  if (e.currentTarget.src !== offerFallback) {
                                    e.currentTarget.src = offerFallback;
                                  }
                                }}
                              />
                            ) : (
                              'GC'
                            )}
                          </OfferIcon>
                          <OfferContent>
                            <OfferTitle>{offer.productName || offer.title}</OfferTitle>
                            <OfferPoints>
                              {(offer.sellingPrice || 0).toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '18px', height: '18px', verticalAlign: 'text-bottom' }} />
                            </OfferPoints>
                            <OfferRedeemBtn 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOfferRedeem(offer);
                              }}
                            >
                              Redeem Now
                            </OfferRedeemBtn>
                          </OfferContent>
                        </OfferCard>
                      );
                    })}
                  </OffersGrid>
                )}
              </RecommendedSection>
            )}

            <QuickActionsSection>
              <SectionTitle>Quick Actions</SectionTitle>
              <QuickActions>
                {quickActions.map((action, index) => (
                  <ActionCard
                    key={action.id}
                    onClick={() => handleActionClick(action.id)}
                    delay={`${index * 0.06}s`}
                  >
                    <ActionIconWrapper>
                      {action.icon}
                    </ActionIconWrapper>
                    <ActionTextGroup>
                      <ActionTitle>{action.title}</ActionTitle>
                    </ActionTextGroup>
                    <ActionChevron>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
                    </ActionChevron>
                  </ActionCard>
                ))}
              </QuickActions>
            </QuickActionsSection>

            {/* <RecommendedSection>
              <RecommendedTitle>Favorite Brands</RecommendedTitle>
              {loadingFavorites ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>
                  Loading favorites...
                </div>
              ) : favoriteBrands.length > 0 ? (
                <FavoriteBrandsContainer>
                  <BrandCardsStack>
                    {favoriteBrands.map((fav, index) => {
                      const brandName = fav.attributes?.brandName || fav.brandName;
                      const brandImage = getBrandImageUrl(fav) || buildInlinePlaceholder(brandName || 'Brand');
                      return (
                        <BrandCard
                          key={fav.id}
                          onClick={() => handleFavoriteBrandClick(brandName)}
                        >
                          <BrandCardImage $src={brandImage} />
                          <BrandCardName>{brandName}</BrandCardName>
                        </BrandCard>
                      );
                    })}
                  </BrandCardsStack>
                </FavoriteBrandsContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>
                  No favorite brands yet. Visit the catalog to add up to 3 favorites!
                </div>
              )}
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <button
                  onClick={() => onNavigate('catalog')}
                  style={{
                    padding: '12px 32px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#ffffff',
                    border: '2px solid rgba(102, 126, 234, 0.6)',
                    borderRadius: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    transition: 'all 0.3s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  Browse Catalog
                </button>
              </div>
            </RecommendedSection> */}

            <RecommendedSection>
              <RecommendedTitle>
                {favoriteBrands.length > 0 ? 'You Might Also Like' : 'Recommended for You'}
              </RecommendedTitle>
              {loadingRecommended ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>
                  Loading recommendations...
                </div>
              ) : recommendedProducts.length > 0 ? (
                <GameGrid>
                  {recommendedProducts.map((product, index) => {
                    const fallbackImage = buildInlinePlaceholder(product.productName || product.title || 'Product');
                    const productImage = getProductImageUrl(product) || fallbackImage;
                    return (
                      <GameCard 
                        key={product.id}
                        onClick={() => handleOfferCardClick(product)}
                      >
                        <GameIcon 
                          src={productImage}
                          alt={product.productName || product.title} 
                          onError={(e) => {
                            if (e.currentTarget.src !== fallbackImage) {
                              e.currentTarget.src = fallbackImage;
                            }
                          }}
                        />
                        <GameName>
                          {product.productName || product.title}
                          <GameCardPrice>
                            {product.sellingPrice?.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '18px', height: '18px', verticalAlign: 'text-bottom' }} />
                          </GameCardPrice>
                        </GameName>
                      </GameCard>
                    );
                  })}
                </GameGrid>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>
                  No recommendations available. Visit the catalog to discover more!
                </div>
              )}
            </RecommendedSection>
          </ContentArea>
        </DashboardLayout>
      </DashboardContent>
    </DashboardContainer>
  );
};

export default Dashboard;